/**
 * Instrument constants for directory listings
 * Used for filtering schools by instruments taught and rehearsal rooms by available equipment
 */

import {
  Piano,
  Guitar,
  Drum,
  Mic2,
  Music,
  Music2,
  Headphones,
  Radio,
  AudioLines,
  type LucideIcon
} from 'lucide-react';

export interface Instrument {
  id: string;
  label: string;
  labelShort: string;
  icon: LucideIcon;
  color: string;
}

export const INSTRUMENTS: Record<string, Instrument> = {
  piano: {
    id: 'piano',
    label: 'Piano',
    labelShort: 'Piano',
    icon: Piano,
    color: '#1E40AF', // blue-800
  },
  guitarra: {
    id: 'guitarra',
    label: 'Guitarra',
    labelShort: 'Guitarra',
    icon: Guitar,
    color: '#B45309', // amber-700
  },
  bateria: {
    id: 'bateria',
    label: 'Batería',
    labelShort: 'Batería',
    icon: Drum,
    color: '#DC2626', // red-600
  },
  bajo: {
    id: 'bajo',
    label: 'Bajo',
    labelShort: 'Bajo',
    icon: Guitar,
    color: '#7C3AED', // violet-600
  },
  violin: {
    id: 'violin',
    label: 'Violín',
    labelShort: 'Violín',
    icon: Music2,
    color: '#0D9488', // teal-600
  },
  canto: {
    id: 'canto',
    label: 'Canto',
    labelShort: 'Canto',
    icon: Mic2,
    color: '#EC4899', // pink-500
  },
  saxofon: {
    id: 'saxofon',
    label: 'Saxofón',
    labelShort: 'Saxo',
    icon: Music,
    color: '#F59E0B', // amber-500
  },
  flauta: {
    id: 'flauta',
    label: 'Flauta',
    labelShort: 'Flauta',
    icon: Music,
    color: '#06B6D4', // cyan-500
  },
  trompeta: {
    id: 'trompeta',
    label: 'Trompeta',
    labelShort: 'Trompeta',
    icon: Music,
    color: '#EAB308', // yellow-500
  },
  produccion: {
    id: 'produccion',
    label: 'Producción Musical',
    labelShort: 'Producción',
    icon: AudioLines,
    color: '#8B5CF6', // violet-500
  },
  dj: {
    id: 'dj',
    label: 'DJ / Mezcla',
    labelShort: 'DJ',
    icon: Headphones,
    color: '#10B981', // emerald-500
  },
  todos: {
    id: 'todos',
    label: 'Múltiples instrumentos',
    labelShort: 'Varios',
    icon: Radio,
    color: '#6B7280', // gray-500
  },
};

export const INSTRUMENT_LIST = Object.values(INSTRUMENTS);

// For filtering - most common instruments first
export const INSTRUMENT_FILTER_ORDER = [
  'piano',
  'guitarra',
  'bateria',
  'canto',
  'bajo',
  'violin',
  'saxofon',
  'produccion',
  'dj',
  'flauta',
  'trompeta',
];

/**
 * Get instruments from a listing
 */
export function getInstrumentInfo(instrumentId: string): Instrument | undefined {
  return INSTRUMENTS[instrumentId];
}

/**
 * Get display chips for instruments (max 3, then "+N más")
 */
export function getInstrumentChips(instruments: string[], maxDisplay = 3): {
  displayed: Instrument[];
  remaining: number;
} {
  const validInstruments = instruments
    .map(id => INSTRUMENTS[id])
    .filter(Boolean);

  return {
    displayed: validInstruments.slice(0, maxDisplay),
    remaining: Math.max(0, validInstruments.length - maxDisplay),
  };
}
